/**
 * LetterIntakeView - the preparer's paste-text intake form for a
 * regulator letter. POSTs to /v1/framework/letters via
 * useIntakeLetter; on success shows the new letter id + a reset
 * action so the preparer can paste another without re-navigating.
 *
 * Position gate (mirrors the backend require_position("preparer")):
 *   - useMe is loading -> render a small skeleton
 *   - not authenticated -> hidden (route guard would 401; this view
 *     is reachable only from the framework module so the user is
 *     already past the auth wall)
 *   - authenticated but no preparer position + not admin -> show a
 *     polite "preparer-only" notice, don't render the form. Mirrors the
 *     backend's 403 + spares the user a submit attempt.
 *
 * Validation: handlers fall through to backend Pydantic for the
 * authoritative shape (extra="forbid" catches typos). The frontend
 * does minimal HTML5 required/maxLength so the operator gets instant
 * feedback on missing fields without a server round-trip.
 *
 * Lives in framework/ - reads framework data + writes via framework
 * hooks. Mounted at /framework/letters/new in App.tsx.
 */
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useDateInputLang } from "shared/i18n/dateInputLang";

import {
  useExtractLetterPdf,
  useIntakeLetter,
  useUploadLetterPdf,
} from "framework/api/hooks";
import type { ExtractedLetter } from "shared/api/types";
import { EngineBusy } from "shared/components/EngineBusy";
import { hasPosition, useMe } from "shared/api/useMe";

interface FormState {
  regulator_code: string;
  title: string;
  received_at: string; // datetime-local string (no TZ); converted on submit
  source_text: string;
}

const EMPTY: FormState = {
  regulator_code: "",
  title: "",
  received_at: "",
  source_text: "",
};

function nowLocalDatetime(): string {
  // The datetime-local input wants "YYYY-MM-DDTHH:MM" in LOCAL time.
  // Default to now so the operator can submit a fresh paste without
  // futzing with the date picker.
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}`
  );
}

export function LetterIntakeView() {
  const { t } = useTranslation();
  const dateInputLang = useDateInputLang();
  const me = useMe();
  const intake = useIntakeLetter();
  const [form, setForm] = useState<FormState>({
    ...EMPTY,
    received_at: nowLocalDatetime(),
  });
  const upload = useUploadLetterPdf();
  const [pdf, setPdf] = useState<File | null>(null);
  // B8 -- what the Engine read, held for review. Nothing is recorded
  // until the person responsible presses Create.
  const extract = useExtractLetterPdf();
  const [review, setReview] = useState<ExtractedLetter | null>(null);

  if (me.isLoading) {
    return (
      <div className="text-sm text-slate-500">
        {t("screens.letterIntakeView.loading")}
      </div>
    );
  }

  // Preparer-only (admin implicit-pass). Render the polite block, not the
  // form - the backend would 403 a submit anyway and surfacing that
  // mid-paste is a worse UX than not showing the form at all.
  if (!hasPosition(me.data, "preparer")) {
    return (
      <article className="prose max-w-3xl">
        <h1 className="text-2xl font-semibold">{t("letters.intake.heading")}</h1>
        <p className="mt-3 text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
          {t("letters.intake.preparerOnly")}
        </p>
      </article>
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // datetime-local -> ISO. new Date(local).toISOString() preserves
    // the LOCAL wall-clock interpretation (the browser knows the TZ),
    // matching what an operator means by "the letter arrived at 09:00".
    const received_at = new Date(form.received_at).toISOString();
    intake.mutate(
      {
        ...form,
        received_at,
        // B8 -- the observations as the person LEFT them. Absent when
        // nothing was extracted, so paste mode is unchanged.
        ...(review ? { observations: review.observations } : {}),
        // D11 -- what it actually is. The review step commits through
        // the same endpoint as a paste, which recorded every uploaded
        // PDF as "pasted" on the record that anchors months of
        // remediation.
        ...(review ? { source_kind: "pdf_upload" as const } : {}),
      },
      {
        // Don't auto-reset on success - the success banner stays
        // visible until the operator hits the reset button so they
        // can see the id of the freshly-created letter.
      },
    );
  }

  function onReset() {
    intake.reset();
    upload.reset();
    extract.reset();
    setReview(null);
    setPdf(null);
    setForm({ ...EMPTY, received_at: nowLocalDatetime() });
  }

  // ── Success view: keep visible until reset clicked ───────────────
  const created = intake.data ?? upload.data;
  if (intake.isSuccess || upload.isSuccess) {
    return (
      <article className="prose max-w-3xl">
        <h1 className="text-2xl font-semibold">{t("letters.intake.heading")}</h1>
        <p
          className="mt-3 text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3"
          role="status"
        >
          {t("letters.intake.success", { title: created?.title ?? "" })}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Link
            data-testid="intake-open-letter"
            to={`/framework/letters/${created?.id ?? ""}`}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-2 text-sm font-medium text-brand-brassDark hover:bg-brand-brass/10"
          >
            {t("letters.intake.viewLetter")}
          </Link>
          <button
            type="button"
            className="px-3 py-2 rounded bg-slate-900 text-white text-sm hover:bg-slate-700"
            onClick={onReset}
          >
            {t("letters.intake.reset")}
          </button>
        </div>
      </article>
    );
  }

  const label = "block text-sm font-medium text-slate-700";
  const help = "text-xs text-slate-500 mt-1";
  const input =
    "mt-1 block w-full rounded border border-slate-300 px-3 py-2 " +
    "text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500";

  return (
    <article className="prose max-w-3xl">
      <h1 className="text-2xl font-semibold">{t("letters.intake.heading")}</h1>
      <p className="mt-2 text-sm text-slate-600">{t("letters.intake.subheading")}</p>

      {/* D4 -- the Framework tile promises "Upload a regulator PDF;
          Marsad Engine extracts observations", and the endpoint has
          worked since the letter track shipped. Nothing in the SPA
          reached it, so this page offered paste-text only and the tile
          described a capability with no route to it. */}
      <section
        data-testid="letter-intake-pdf"
        className="mt-5 rounded border border-brand-brass/40 bg-brand-brass/5 p-3"
      >
        <p className="text-sm font-medium text-slate-800">
          {t("letters.intake.pdfHeading")}
        </p>
        <p className="mt-0.5 text-xs text-slate-600">{t("letters.intake.pdfBody")}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept="application/pdf,.pdf"
            data-testid="letter-intake-pdf-file"
            onChange={(e) => setPdf(e.target.files?.[0] ?? null)}
            className="text-xs"
          />
          <button
            type="button"
            data-testid="letter-intake-pdf-submit"
            disabled={!pdf || extract.isPending}
            onClick={() => {
              if (!pdf) return;
              extract.mutate(
                { file: pdf },
                {
                  onSuccess: (data) => {
                    setReview(data);
                    setForm((f) => ({
                      ...f,
                      // B8 -- the review has to land READY to commit.
                      // The one-shot upload defaulted the regulator to
                      // SAMA; the form leaves it blank and required, so
                      // without this the reviewed letter could not be
                      // created and the button would simply do nothing.
                      // Same default as before, and still editable.
                      regulator_code: f.regulator_code.trim() || "SAMA",
                      title: data.proposed_title,
                      source_text: data.raw_text,
                      // D11 -- cleared, not prefilled with now.
                      //
                      // The field is labelled the LETTER's date and was
                      // pre-populated with the moment of upload, so a
                      // letter dated three weeks ago was recorded as
                      // arriving today unless somebody noticed. Blank
                      // is honest and the field is required, so it has
                      // to be answered rather than accepted by
                      // default. Paste mode keeps today's date, where
                      // "it arrived just now" is usually true.
                      //
                      // Round 16 A26 -- and filled from the letter where
                      // it states its own date: the person was typing a
                      // date the letter had already printed. Midnight,
                      // because the letter says a day and not a time.
                      received_at: data.letter_date ? `${data.letter_date}T00:00` : "",
                    }));
                  },
                },
              );
            }}
            className="rounded bg-brand-brass px-3 py-1.5 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {extract.isPending ? (
              <EngineBusy label={t("letters.intake.pdfWorking")} />
            ) : (
              t("letters.intake.pdfSubmit")
            )}
          </button>
        </div>
        {extract.isError && (
          <p
            data-testid="letter-intake-pdf-error"
            className="mt-2 text-xs text-rose-700"
          >
            {extract.error.status === 503
              ? t("letters.intake.pdfEngineOff")
              : extract.error.detail}
          </p>
        )}
        {/* B8 -- the review. The title, the date and every observation
            are editable here, and the original text is linked, because
            a letter anchors months of remediation and this is the only
            moment at which the person responsible can say "that is not
            what it says". */}
        {review && (
          <div data-testid="letter-intake-review" className="mt-3 space-y-2">
            <p className="text-xs text-slate-600">
              {t("letters.intake.reviewLead", {
                count: review.observations.length,
              })}
            </p>
            <ul className="space-y-2">
              {review.observations.map((o, i) => (
                <li
                  key={o.sequence}
                  data-testid={`letter-intake-obs-${o.sequence}`}
                  className="rounded border border-paper-2 p-2"
                >
                  <input
                    dir="auto"
                    aria-label={t("letters.intake.obsTitle")}
                    data-testid={`letter-intake-obs-title-${o.sequence}`}
                    value={o.title}
                    onChange={(e) =>
                      setReview((r) =>
                        r
                          ? {
                              ...r,
                              observations: r.observations.map((x, j) =>
                                j === i ? { ...x, title: e.target.value } : x,
                              ),
                            }
                          : r,
                      )
                    }
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                  />
                  <textarea
                    dir="auto"
                    aria-label={t("letters.intake.obsBody")}
                    data-testid={`letter-intake-obs-body-${o.sequence}`}
                    value={o.body}
                    rows={2}
                    onChange={(e) =>
                      setReview((r) =>
                        r
                          ? {
                              ...r,
                              observations: r.observations.map((x, j) =>
                                j === i ? { ...x, body: e.target.value } : x,
                              ),
                            }
                          : r,
                      )
                    }
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                  />
                  <button
                    type="button"
                    data-testid={`letter-intake-obs-drop-${o.sequence}`}
                    onClick={() =>
                      setReview((r) =>
                        r
                          ? {
                              ...r,
                              observations: r.observations.filter((_, j) => j !== i),
                            }
                          : r,
                      )
                    }
                    className="mt-1 text-[11px] text-rose-700 hover:underline"
                  >
                    {t("letters.intake.obsDrop")}
                  </button>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500">
              {t("letters.intake.reviewThenCreate")}
            </p>
          </div>
        )}
      </section>

      <form className="mt-6 space-y-5" onSubmit={onSubmit}>
        <div>
          <label htmlFor="regulator_code" className={label}>
            {t("letters.intake.field.regulator_code")}
          </label>
          <input
            dir="auto"
            id="regulator_code"
            name="regulator_code"
            type="text"
            required
            maxLength={32}
            value={form.regulator_code}
            onChange={(e) => setForm({ ...form, regulator_code: e.target.value })}
            className={input}
          />
          <p className={help}>{t("letters.intake.field.regulator_code_help")}</p>
        </div>

        <div>
          <label htmlFor="title" className={label}>
            {t("letters.intake.field.title")}
          </label>
          <input
            dir="auto"
            id="title"
            name="title"
            type="text"
            required
            maxLength={512}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className={input}
          />
          <p className={help}>{t("letters.intake.field.title_help")}</p>
        </div>

        <div>
          <label htmlFor="received_at" className={label}>
            {t("letters.intake.field.received_at")}
          </label>
          <input
            dir="auto"
            id="received_at"
            name="received_at"
            type="datetime-local"
            lang={dateInputLang}
            required
            value={form.received_at}
            onChange={(e) => setForm({ ...form, received_at: e.target.value })}
            className={input}
          />
          <p className={help}>{t("letters.intake.field.received_at_help")}</p>
          {review?.letter_date && form.received_at.startsWith(review.letter_date) && (
            <p data-testid="letter-intake-date-from-letter" className={help}>
              {t("letters.intake.field.received_at_from_letter")}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="source_text" className={label}>
            {t("letters.intake.field.source_text")}
          </label>
          <textarea
            dir="auto"
            id="source_text"
            name="source_text"
            required
            rows={10}
            value={form.source_text}
            onChange={(e) => setForm({ ...form, source_text: e.target.value })}
            className={input}
          />
          <p className={help}>{t("letters.intake.field.source_text_help")}</p>
        </div>

        {intake.isError && (
          <p
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3"
            role="alert"
          >
            {intake.error.detail}
          </p>
        )}

        <button
          type="submit"
          data-testid="letter-intake-submit"
          disabled={intake.isPending}
          className={[
            "px-4 py-2 rounded text-sm font-medium",
            "bg-brand-brass text-brand-navy hover:bg-brand-brassDark",
            "disabled:bg-slate-400 disabled:cursor-not-allowed",
          ].join(" ")}
        >
          {intake.isPending
            ? t("letters.intake.submitting")
            : t("letters.intake.submit")}
        </button>
      </form>
    </article>
  );
}
